import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import Spinner from '../../../components/spinner/Spinner';
import { PARAM } from '../../../constants/reading/ApiConstants';
import NwfProbeDetails from './nwf/NwfProbeDetails';
import BasicComprehensionProbeDetails from '../shared/basicComprehension/BasicComprehensionProbeDetails';
import RANProbeDetails from './ran/RANProbeDetails';
import { isDefaultTypeResult } from '../../../utils/shared/probeDetailUtils';
import WordLetterProbeDetails from '../shared/wordLetter/WordLetterProbeDetails';
import OralReadingProbeDetails from '../shared/oralReading/OralReadingProbeDetails';
import PhonemicAwarenessProbeDetails from '../shared/phonemicAwareness/PhonemicAwarenessProbeDetails';
import SpellingProbeDetails from '../shared/spelling/SpellingProbeDetails';
import WordBox from '../shared/WordBox';

export const NOT_AVAILABLE_MESSAGE_FOR_PD = 'Probe details are not available for this result.';
export const IMPORTED_RESULT_MESSAGE_FOR_PD =
    'No details available. This result was added using Assessment Importer.';
export const SECURE_RESULT_MESSAGE_FOR_PD =
    'No details available. This measure was conducted with secure assessment administration.';

const { GENERATING_RESULT_BY_SCRIPT, GENERATING_RESULT_BY_ASSESSMENT_IMPORTER, SECURE_ASSESSMENT } =
    PARAM;

const PROBE_DETAILS_BODY_RENDER = {
    renderNWFProbeDetails: (result, content) => (
        <NwfProbeDetails result={result} content={content} />
    ),
    renderMAZEProbeDetails: (result, content) => (
        <BasicComprehensionProbeDetails result={result} content={content} />
    ),
    renderRANProbeDetails: (result, content) => (
        <RANProbeDetails result={result} content={content} />
    ),
    renderLNFProbeDetails: (result, content) => (
        <WordLetterProbeDetails content={content} result={result} RowCell={WordBox} />
    ),
    renderWRFProbeDetails: (result, content) => (
        <WordLetterProbeDetails content={content} result={result} RowCell={WordBox} />
    ),
    renderORFProbeDetails: (result, content) => (
        <OralReadingProbeDetails result={result} content={content} />
    ),
    renderPSFProbeDetails: (result, content) => (
        <PhonemicAwarenessProbeDetails result={result} content={content} />
    ),
    renderSPELLINGProbeDetails: (result, content) => (
        <SpellingProbeDetails result={result} content={content} />
    ),
};

const ProbeDetailsBody = ({ isFormFetching, content, fetchFormData, result }) => {
    useEffect(() => {
        if (
            !isFormFetching &&
            isEmpty(content) &&
            isDefaultTypeResult(result.dataSource) &&
            !result.isSecure
        ) {
            fetchFormData();
        }
    }, []);

    const getDisplayingMessage = message => (
        <h4 data-test-id="pd-message" className="d-flex justify-content-center p-4">
            {message}
        </h4>
    );

    switch (result.dataSource) {
        case GENERATING_RESULT_BY_SCRIPT:
            return getDisplayingMessage(NOT_AVAILABLE_MESSAGE_FOR_PD);
        case GENERATING_RESULT_BY_ASSESSMENT_IMPORTER:
            return getDisplayingMessage(IMPORTED_RESULT_MESSAGE_FOR_PD);
        case SECURE_ASSESSMENT:
            return getDisplayingMessage(SECURE_RESULT_MESSAGE_FOR_PD);

        default:
            if (isEmpty(content)) return <Spinner />;
    }

    return PROBE_DETAILS_BODY_RENDER[`render${result.measure}ProbeDetails`](result, content);
};

ProbeDetailsBody.defaultProps = {
    result: {},
    content: {},
    isFormFetching: false,
    fetchFormData: () => {},
};

ProbeDetailsBody.propTypes = {
    result: PropTypes.object,
    content: PropTypes.object,
    fetchFormData: PropTypes.func,
    isFormFetching: PropTypes.bool,
};

export default ProbeDetailsBody;
